import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, MessageCircle, FileText, Shield, Mail, Phone, Search } from 'lucide-react';

const TelecallerHelpSupport = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'general', label: 'General', icon: HelpCircle },
    { id: 'leads', label: 'Leads', icon: MessageCircle },
    { id: 'orders', label: 'Orders', icon: FileText },
    { id: 'technical', label: 'Technical', icon: Shield },
  ];

  const faqs = [
    {
      id: 1,
      category: 'general',
      question: 'How do I log a call?',
      answer: 'Open the Dialer tab, select a lead, enter the call status and notes, then click "End Call" to log the call activity.',
    },
    {
      id: 2,
      category: 'general',
      question: 'How do I confirm a lead?',
      answer: 'Go to the Confirmation tab, select the leads you want to confirm, choose the confirmation status, add notes if needed, and click "Confirm Selected".',
    },
    {
      id: 3,
      category: 'leads',
      question: 'What is the difference between assigned leads and booked leads?',
      answer: 'Assigned leads are leads that have been assigned to you. Booked leads are confirmed leads that have been converted to orders.',
    },
    {
      id: 4,
      category: 'orders',
      question: 'How do I view order details?',
      answer: 'Go to the Booked tab and click on any order card to view detailed information including customer details, payment info, and tracking status.',
    },
    {
      id: 5,
      category: 'technical',
      question: 'How do I access call recordings?',
      answer: 'Go to More > Call Recordings to view all your call recordings. You can filter by status and search by customer name or phone number.',
    },
  ];

  const filteredFAQs = faqs.filter((faq) => {
    const matchesCategory = selectedCategory === 'general' || faq.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const supportContacts = [
    { type: 'Email', value: 'support@roadserve.com', icon: Mail },
    { type: 'Phone', value: '+91 1234567890', icon: Phone },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/telecaller/more')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Help & Support</h1>
          <p className="text-gray-600 mt-1">Get help and contact support</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-lg border-2 transition ${
                  selectedCategory === category.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon
                  size={24}
                  className={`mx-auto mb-2 ${
                    selectedCategory === category.id ? 'text-blue-600' : 'text-gray-400'
                  }`}
                />
                <p className={`text-sm font-medium ${
                  selectedCategory === category.id ? 'text-blue-600' : 'text-gray-700'
                }`}>
                  {category.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
        {filteredFAQs.length === 0 ? (
          <div className="text-center py-8">
            <HelpCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No FAQs found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <div key={faq.id} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                <p className="text-sm text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Support */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Support</h3>
        <div className="space-y-4">
          {supportContacts.map((contact, index) => {
            const Icon = contact.icon;
            return (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{contact.type}</p>
                    <p className="text-sm text-gray-600">{contact.value}</p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">
                  Contact
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Resources */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Resources</h3>
        <div className="space-y-3">
          <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText size={20} className="text-gray-400" />
              <span className="text-gray-900">User Guide</span>
            </div>
            <span className="text-gray-400">{'>'}</span>
          </button>
          <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Shield size={20} className="text-gray-400" />
              <span className="text-gray-900">Privacy Policy</span>
            </div>
            <span className="text-gray-400">{'>'}</span>
          </button>
          <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText size={20} className="text-gray-400" />
              <span className="text-gray-900">Terms of Service</span>
            </div>
            <span className="text-gray-400">{'>'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TelecallerHelpSupport;

